import _ from 'lodash'
import { Component, Structure } from '../types'
import type {
	LayoutRefs,
	MeshItemLayout,
	PageJson,
	SingleLayoutData,
	TransformationsDataMap,
	TransformData,
	UnresolvedVariantRelation,
} from '@wix/thunderbolt-becky-types'

type StructureAndData = { data: PageJson['data']; structure: Structure }

const fixLayout = (
	layoutData: PageJson['data']['layout_data'],
	layoutItmToCompId: Record<string, Component>
): PageJson['data']['layout_data'] => {
	const isFitToContentHeightComponent = (compType: string, singleLayout: SingleLayoutData) => {
		return (
			compType.includes('RefComponent') ||
			compType.includes('MenuComponent') ||
			compType.includes('Page') ||
			compType.includes('BoxSlideShowSlide') ||
			compType.includes('StripContainerSlideShowSlide') ||
			(compType.includes('PopupContainer') &&
				singleLayout?.itemLayout?.type === 'FixedItemLayout' &&
				singleLayout?.itemLayout.margins?.top &&
				singleLayout?.itemLayout.margins?.bottom)
		)
	}

	return _.mapValues(layoutData, (layout) => {
		if (layout.type === 'SingleLayoutData') {
			const layoutId = layout.id

			const comp = layoutItmToCompId[layoutId]

			if (!comp) {
				return layout
			}
			const compType = comp.componentType
			const absLayout = comp.layout

			let newLayout = { ...layout }

			if (_.get(newLayout, ['itemLayout', 'type'], '') === 'MeshItemLayout') {
				const itemLayout = newLayout.itemLayout! as MeshItemLayout

				const isStretched = itemLayout.justifySelf === 'stretch' || itemLayout.justifySelf === 'center'
				newLayout = {
					...newLayout,
					itemLayout: {
						type: 'MeshItemLayout',
						absoluteRect: {
							left: absLayout.x,
							top: absLayout.y,
							width: absLayout.width,
							height: absLayout.height,
							rotationInDegrees: absLayout.rotationInDegrees,
						},
						justifySelf: itemLayout.justifySelf,
						margins: isStretched ? _.pick(itemLayout.margins, ['left', 'right']) : {},
					},
				} as SingleLayoutData
			}

			if (_.get(newLayout, ['containerLayout', 'type'], '') === 'MeshContainerLayout') {
				newLayout = {
					...newLayout,
					containerLayout: {
						...newLayout.containerLayout,
						type: 'MeshContainerLayout',
						containerHeight: absLayout.height,
					},
				} as SingleLayoutData
			}

			if (compType.includes('FooterContainer') && newLayout.itemLayout?.type === 'FixedItemLayout') {
				newLayout = ({
					...newLayout,
					itemLayout: {
						type: 'FixedPositionItemLayout',
						bottom: {
							type: 'px',
							value: 0,
						},
					},
					componentLayout: {
						...newLayout.componentLayout,
						width: {
							type: 'percentage',
							value: 100,
						},
					},
				} as unknown) as SingleLayoutData
			}

			// should not have meshItemLayout
			if (compType.includes('PagesContainer')) {
				newLayout = ({
					...newLayout,
					itemLayout: {},
				} as unknown) as SingleLayoutData
			}

			if (isFitToContentHeightComponent(compType, newLayout)) {
				newLayout = {
					...newLayout,
					containerLayout: {
						...newLayout.containerLayout,
						containerHeight: 'auto',
					},
				} as SingleLayoutData
			}

			if (compType.includes('AppController')) {
				newLayout = {
					...newLayout,
					itemLayout: {
						...newLayout.itemLayout,
						isAbsolutePosition: true,
					},
				} as SingleLayoutData
			}

			if (compType.includes('RefComponent')) {
				if (compType.includes('HeaderContainer') && newLayout.itemLayout?.type === 'FixedItemLayout') {
					newLayout = ({
						...newLayout,
						itemLayout: {
							type: 'FixedPositionItemLayout',
							top: {
								type: 'px',
								value: 0,
							},
							margins: {
								top: {
									type: 'SystemVariable',
									value: 'topBannerHeight',
								},
							},
						},
						componentLayout: {
							...newLayout.componentLayout,
							width: {
								type: 'percentage',
								value: 100,
							},
						},
					} as unknown) as SingleLayoutData
				}
			}

			if (comp.id === 'BACK_TO_TOP_BUTTON') {
				newLayout = {
					...newLayout,
					itemLayout: {
						type: 'FixedPositionItemLayout',
						bottom: {
							type: 'px',
							value: 20,
						},
						right: {
							type: 'px',
							value: 20,
						},
					},
				} as SingleLayoutData
			}

			if (comp.id === 'QUICK_ACTION_BAR') {
				newLayout = {
					...newLayout,
					itemLayout: {
						type: 'FixedPositionItemLayout',
						bottom: {
							type: 'px',
							value: 0,
						},
						left: {
							type: 'px',
							value: 0,
						},
					},
					componentLayout: {
						type: 'ClassicComponentLayout',
						width: {
							type: 'SystemVariable',
							value: 'siteWidth',
						},
					},
				} as SingleLayoutData
			}

			if (comp.id === 'MENU_AS_CONTAINER') {
				newLayout = ({
					...newLayout,
					componentLayout: {
						type: 'ClassicComponentLayout',
						width: {
							type: 'px',
							value: 254,
						},
						height: {
							type: 'vh',
							value: 100,
						},
					},
					itemLayout: {
						type: 'FixedPositionItemLayout',
						right: {
							type: 'CalcFormula',
							value: {
								// eslint-disable-next-line no-template-curly-in-string
								formula: '(100% - ${siteWidth}) / 2',
								values: {
									siteWidth: {
										type: 'SystemVariable',
										value: 'siteWidth',
									},
								},
							},
						},
					},
				} as unknown) as SingleLayoutData
			}

			if (comp.id === 'MENU_AS_CONTAINER_TOGGLE' && newLayout.itemLayout?.type === 'FixedItemLayout') {
				newLayout = {
					...newLayout,
					itemLayout: {
						type: 'FixedPositionItemLayout',
						top: {
							type: 'px',
							value: 10,
						},
						left: {
							type: 'px',
							value: 259,
						},
						margins: {
							top: {
								type: 'SystemVariable',
								value: 'topBannerHeight',
							},
						},
					},
					componentLayout: {
						type: 'ClassicComponentLayout',
						width: {
							type: 'px',
							value: 50,
						},
						height: {
							type: 'px',
							value: 50,
						},
					},
				} as SingleLayoutData
			}

			return newLayout
		}

		return layout
	})
}

const getLayoutItmToCompId = ({ structure, data }: StructureAndData): Record<string, Component> => {
	const compIdToDefaultLayout = _.mapValues(structure, (comp) => {
		// SITE_PAGES is missing layoutQuery
		if (!comp.layoutQuery) {
			return { layouts: [], comp }
		}

		const layoutId = comp.layoutQuery!.replace('#', '')
		const refArray = data.layout_data[layoutId]
		const layoutItems = (refArray as LayoutRefs).values.map((v) => v.replace('#', ''))

		const mobileLayouts = []
		if (layoutItems.length === 2) {
			mobileLayouts.push((data.layout_data[layoutItems[1]] as UnresolvedVariantRelation).to.replace('#', ''))
		} else if (data.layout_data[layoutItems[0]].type === 'VariantRelation') {
			mobileLayouts.push((data.layout_data[layoutItems[0]] as UnresolvedVariantRelation).to.replace('#', ''))
		}

		return {
			layouts: [refArray.id, ...layoutItems, ...mobileLayouts],
			comp,
		}
	})
	return _.values(compIdToDefaultLayout).reduce<Record<string, Component>>((acc, { comp, layouts }) => {
		layouts.forEach((v) => {
			acc[v] = comp
		})
		return acc
	}, {})
}

const fixStructure = (flatStructure: StructureAndData['structure']): StructureAndData['structure'] => {
	return _.mapValues(flatStructure, (comp) => {
		return {
			...comp,
			layout: {},
		}
	})
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const fixTransformationsData = (
	layoutData: PageJson['data']['layout_data'],
	layoutItemToComp: Record<string, Component>,
	flatStructure: Structure
) => {
	const fixedStructure: Structure = { ...flatStructure }
	const transformationsData: TransformationsDataMap = {}

	_.forEach(layoutData, (layout, layoutId) => {
		const rotationInDegrees = _.get(layout, ['componentLayout', 'rotationInDegrees'])
		if (layout.type === 'SingleLayoutData' && rotationInDegrees) {
			const transformationId = `transformation${layoutId.replace('layout', '')}`
			const compTransformationQuery = fixedStructure[layoutItemToComp[layoutId].id].transformationQuery

			if (!compTransformationQuery) {
				transformationsData[`${transformationId}-ref-array`] = {
					id: `${transformationId}-ref-array`,
					type: 'RefArray',
					// @ts-ignore
					values: [`#${transformationId}`],
				}

				fixedStructure[layoutItemToComp[layoutId].id].transformationQuery = `${transformationId}-ref-array`
			} else {
				// @ts-ignore
				transformationsData[`${transformationId}-variant-relation`] = {
					id: `${transformationId}-variant-relation`,
					type: 'VariantRelation',
					from: `#${layoutItemToComp[layoutId].id}`,
					to: `#${transformationId}`,
					variants: ['#MOBILE-VARIANT'],
				} as UnresolvedVariantRelation

				const transformationRefArray = transformationsData[compTransformationQuery]
				// @ts-ignore
				transformationRefArray.values.push(`${transformationId}-variant-relation`)
			}

			transformationsData[transformationId] = {
				id: transformationId,
				type: 'TransformData',
				rotate: rotationInDegrees + (compTransformationQuery ? 10 : 0),
			} as TransformData
		}
	})

	return { transformationsData, fixedStructure }
}

export const oneDocFixerForPage = (structureAndData: StructureAndData): StructureAndData => {
	const layoutItmToCompId = getLayoutItmToCompId(structureAndData)
	const { data, structure } = structureAndData

	const fixedLayoutData = fixLayout(data.layout_data, layoutItmToCompId)
	// const { transformationsData, fixedStructure } = fixTransformationsData(
	// 	fixedLayoutData,
	// 	layoutItmToCompId,
	// 	structure
	// )
	const fixedFlatStructure = fixStructure(structure)

	return {
		structure: fixedFlatStructure,
		data: {
			...data,
			layout_data: fixedLayoutData,
			// transformations_data: transformationsData,
		},
	}
}

export default oneDocFixerForPage
